// Static, opt-in script gating (Approach A).
//
// We do NOT intercept arbitrary scripts (no document.createElement override, no
// src-setter trap). Instead, scripts the user explicitly tags are held until the
// relevant category is granted:
//
//   <script type="text/plain" data-tc-category="marketing" data-tc-src="https://..."></script>
//   <script type="text/plain" data-tc-category="statistics">/* inline */</script>
//
// On consent, a tagged node is replaced with a live <script>. Attributes are
// copied across; inline code is moved via textContent. We never inject HTML from
// fetched/remote data — only re-activate markup already present and reviewed on
// the page.

import type { Category, ConsentMap } from './types';

const ACTIVATED = 'data-tc-activated';

/** Activate every gated script whose category is now granted. Idempotent. */
export function activateGatedScripts(consent: Partial<ConsentMap>): void {
  let nodes: HTMLScriptElement[];
  try {
    nodes = Array.prototype.slice.call(
      document.querySelectorAll('script[type="text/plain"][data-tc-category]'),
    ) as HTMLScriptElement[];
  } catch {
    return;
  }

  for (const node of nodes) {
    if (node.getAttribute(ACTIVATED) === '1') continue;
    const cat = node.getAttribute('data-tc-category') as Category | null;
    if (!cat || !consent[cat]) continue;
    try {
      activate(node);
    } catch {
      /* one bad tag shouldn't break the rest */
    }
  }
}

function activate(node: HTMLScriptElement): void {
  const fresh = document.createElement('script');

  // Copy through normal attributes; skip our control attrs and the gating type.
  for (const attr of Array.prototype.slice.call(node.attributes) as Attr[]) {
    const name = attr.name;
    if (name === 'type' || name === 'data-tc-category' || name === 'data-tc-src') continue;
    fresh.setAttribute(name, attr.value);
  }

  const externalSrc = node.getAttribute('data-tc-src') || node.getAttribute('src');
  if (externalSrc) {
    fresh.src = externalSrc;
  } else {
    // Inline code: move as text, never via innerHTML of remote data.
    fresh.text = node.textContent || '';
  }

  node.setAttribute(ACTIVATED, '1');
  node.parentNode?.insertBefore(fresh, node);
  node.parentNode?.removeChild(node);
}
